import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator, RefreshControl, Platform, Image } from 'react-native';
import { InsetModal as Modal } from '../components/InsetModal';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { apiService } from '../services/api';
import { RecentPost } from './dashboard/RecentPostsCard';
import { PostCard } from '../components/PostCard';
import { PostCommentsPanel } from '../components/PostCommentsPanel';
import { PostActionsSheet } from '../components/posts/PostActionsSheet';
import { FacebookInsightsModal } from '../components/FacebookInsightsModal';
import { ScreenLayout } from '../components/ScreenLayout';
import { Header } from '../components/Header';
import { colors, spacing, typography } from '../theme';
import { withAlpha } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { usePermissions } from '../hooks/usePermissions';
import { setPostToEdit } from '../services/postEditing';

// Wider than the shared default pane width (350px) — this screen's right
// pane holds a full post plus its comments feed.
const POSTS_RIGHT_PANE_WIDTH = 480;

type Status = 'published' | 'scheduled' | 'draft' | 'failed';
const STATUSES: Status[] = ['published', 'scheduled', 'draft', 'failed'];
const ALL = 'all';

interface Page { page_id: string; page_name?: string; picture?: string }

/**
 * Page posts: every connected Facebook page (or one), by status —
 * Published · Scheduled · Drafts · Failed — with search. A post opens with
 * its comments (right pane on desktop web, full screen elsewhere); "…"
 * edits, publishes, retries, cancels a schedule or deletes it.
 */
export const PostsListScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab, routeParams } = useAppContext();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { hasPermissionAtLeast } = usePermissions();
  const canManage = hasPermissionAtLeast('fbm.managePosts', 'edit');

  const params = (routeParams || {}) as { pageId?: string; postId?: string; status?: Status };
  const [pages, setPages] = useState<Page[]>([]);
  const [pagesLoaded, setPagesLoaded] = useState(false);
  const [pageFilter, setPageFilter] = useState<string>(params.pageId || ALL);
  const [status, setStatus] = useState<Status>(params.status && STATUSES.includes(params.status) ? params.status : 'published');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [posts, setPosts] = useState<RecentPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showLeftPane, setShowLeftPane] = useState(Platform.OS === 'web');
  const [selectedPost, setSelectedPost] = useState<RecentPost | null>(null);
  const [actionsFor, setActionsFor] = useState<RecentPost | null>(null);
  const [showInsights, setShowInsights] = useState(false);
  const [pendingPostId, setPendingPostId] = useState<string | undefined>(params.postId);

  // Route params change when arriving again (e.g. from the composer)
  useEffect(() => {
    if (params.status && STATUSES.includes(params.status)) setStatus(params.status);
    if (params.pageId) setPageFilter(params.pageId);
    if (params.postId) setPendingPostId(params.postId);
  }, [params.status, params.pageId, params.postId]);

  useEffect(() => {
    setActiveTab('social');
  }, [setActiveTab]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(id);
  }, [query]);

  useEffect(() => {
    apiService.getFacebookPages()
      .then((res) => setPages(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPages([]))
      .finally(() => setPagesLoaded(true));
  }, []);

  const requestId = useRef(0);
  const load = useCallback(async () => {
    const id = ++requestId.current;
    try {
      const res = await apiService.listFacebookPosts({
        pageId: pageFilter === ALL ? undefined : pageFilter,
        status,
        search: debouncedQuery,
        limit: 100,
      });
      if (id !== requestId.current) return; // a newer filter won
      setPosts(Array.isArray(res.data) ? res.data : []);
    } catch {
      if (id === requestId.current) setPosts([]);
    } finally {
      if (id === requestId.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [pageFilter, status, debouncedQuery]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // A deep link (notification) opens its post once it's in the list —
  // published posts are where notifications point.
  useEffect(() => {
    if (!pendingPostId || posts.length === 0) return;
    const match = posts.find((p) => p.postId === pendingPostId);
    if (match) {
      setSelectedPost(match);
      setPendingPostId(undefined);
    }
  }, [pendingPostId, posts]);

  // Keep the open post in step with the reloaded list
  useEffect(() => {
    if (!selectedPost) return;
    const fresh = posts.find((p) => p.postId === selectedPost.postId);
    if (fresh && fresh !== selectedPost) setSelectedPost(fresh);
  }, [posts]);

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const singlePageId = pageFilter !== ALL ? pageFilter : pages.length === 1 ? pages[0].page_id : null;

  // Backfill of posts made on Facebook before DISLIX was connected — new
  // posts sync through the page's feed webhook.
  const onSyncHistory = async () => {
    const targets = singlePageId ? [singlePageId] : pages.map((p) => p.page_id);
    if (targets.length === 0 || syncing) return;
    setSyncing(true);
    try {
      let imported = 0;
      for (const pid of targets) {
        const result = await apiService.importFacebookPosts(pid);
        if (result.statusCode === 1000) imported += result.data?.imported ?? 0;
      }
      showNotification('success', t('posts.synced'), t('posts.syncedCount', { count: imported }));
      await load();
    } catch (error: any) {
      showNotification('error', t('posts.syncFailed'), error?.message || '');
    } finally {
      setSyncing(false);
    }
  };

  const editPost = (post: RecentPost) => {
    setPostToEdit(post);
    navigateTo('post-create', undefined, { postId: post.postId });
  };

  const detail = selectedPost ? (
    selectedPost.status === 'published' && selectedPost.pageId ? (
      <PostCommentsPanel
        post={selectedPost}
        pageId={selectedPost.pageId}
        headerContent={<PostCard post={selectedPost} compact={false} disableCommentsModal onMore={setActionsFor} />}
      />
    ) : (
      <ScrollView contentContainerStyle={styles.detailsScrollContent}>
        <PostCard post={selectedPost} compact={false} disableCommentsModal onMore={setActionsFor} />
      </ScrollView>
    )
  ) : null;

  const emptyText = (() => {
    if (pagesLoaded && pages.length === 0) return t('posts.noPages');
    if (debouncedQuery) return t('posts.noMatches');
    return t(`posts.empty.${status}` as any);
  })();

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onTabPress={handleTabPress}
      showLeftPane={showLeftPane}
      onLeftPaneClose={() => setShowLeftPane(false)}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      rightPaneWidthOverride={POSTS_RIGHT_PANE_WIDTH}
      rightPaneContent="custom"
      rightPaneCustomContent={
        detail ? <View style={styles.detailsContainer}>{detail}</View> : (
          <View style={styles.detailsEmpty}>
            <Ionicons name="newspaper-outline" size={32} color={colors.text.disabled as string} />
            <Text style={styles.detailsEmptyText}>{t('posts.selectPost')}</Text>
          </View>
        )
      }
    >
      <View style={styles.container}>
        <View style={[styles.titleRow, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigateTo('home')} activeOpacity={0.7} accessibilityLabel={t('common.back')}>
            <DirectionalIcon name="chevron-back" size={22} color={colors.text.primary as string} />
          </TouchableOpacity>
          <View style={styles.titleInfo}>
            <Text style={[styles.title, { textAlign: rtl.textStart }]} numberOfLines={1}>{t('home.posts.title')}</Text>
          </View>
          <TouchableOpacity
            style={[styles.iconButton, !singlePageId && styles.iconButtonDisabled]}
            onPress={() => singlePageId && setShowInsights(true)}
            disabled={!singlePageId}
            accessibilityLabel={t('posts.insights')}
          >
            <Ionicons name="stats-chart-outline" size={18} color={colors.text.primary as string} />
          </TouchableOpacity>
          {canManage && (
            <TouchableOpacity
              style={[styles.iconButton, (pages.length === 0 || syncing) && styles.iconButtonDisabled]}
              onPress={onSyncHistory}
              disabled={pages.length === 0 || syncing}
              accessibilityLabel={t('posts.sync')}
            >
              {syncing ? <ActivityIndicator size="small" color={colors.text.primary as string} /> : (
                <Ionicons name="sync-outline" size={18} color={colors.text.primary as string} />
              )}
            </TouchableOpacity>
          )}
          {canManage && (
            <TouchableOpacity
              style={[styles.createButton, { flexDirection: rtl.rowDirection }, pages.length === 0 && styles.iconButtonDisabled]}
              onPress={() => navigateTo('post-create', undefined, singlePageId ? { pageId: singlePageId } : undefined)}
              disabled={pages.length === 0}
            >
              <Ionicons name="add" size={18} color={colors.text.onPrimary as string} />
              <Text style={styles.createText}>{t('home.posts.create')}</Text>
            </TouchableOpacity>
          )}
        </View>

        {pages.length > 1 && (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.pageRow, { flexDirection: rtl.rowDirection }]}>
            {[{ page_id: ALL, page_name: t('posts.allPages') } as Page, ...pages].map((p) => {
              const on = pageFilter === p.page_id;
              return (
                <TouchableOpacity key={p.page_id} style={[styles.pageChip, on && styles.pageChipOn, { flexDirection: rtl.rowDirection }]} onPress={() => setPageFilter(p.page_id)}>
                  {p.page_id === ALL ? (
                    <Ionicons name="albums-outline" size={14} color={(on ? colors.primary : colors.text.secondary) as string} />
                  ) : p.picture ? (
                    <Image source={{ uri: p.picture }} style={styles.pageAvatar} />
                  ) : (
                    <Ionicons name="logo-facebook" size={14} color="#1877F2" />
                  )}
                  <Text style={[styles.pageChipText, on && styles.pageChipTextOn]} numberOfLines={1}>{p.page_name || p.page_id}</Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}

        <View style={[styles.tabs, { flexDirection: rtl.rowDirection }]}>
          {STATUSES.map((s) => (
            <TouchableOpacity
              key={s}
              style={[styles.tab, status === s && styles.tabOn]}
              onPress={() => { setStatus(s); setSelectedPost(null); }}
              accessibilityRole="tab"
              accessibilityState={{ selected: status === s }}
            >
              <Text style={[styles.tabText, status === s && styles.tabTextOn]} numberOfLines={1}>{t(`posts.tab.${s}` as any)}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <View style={[styles.searchBox, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="search-outline" size={16} color={colors.text.secondary as string} />
          <TextInput
            style={[styles.searchInput, { textAlign: rtl.textStart }]}
            placeholder={t('posts.search')}
            placeholderTextColor={colors.text.secondary as string}
            value={query}
            onChangeText={setQuery}
          />
          {query.length > 0 && (
            <TouchableOpacity onPress={() => setQuery('')} hitSlop={8}>
              <Ionicons name="close-circle" size={16} color={colors.text.secondary as string} />
            </TouchableOpacity>
          )}
        </View>

        {loading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={colors.brand.primary as string} />
          </View>
        ) : (
          <ScrollView
            style={styles.list}
            contentContainerStyle={styles.listContent}
            refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} />}
          >
            {posts.length === 0 ? (
              <View style={styles.empty}>
                <Ionicons name={pages.length === 0 && pagesLoaded ? 'logo-facebook' : 'newspaper-outline'} size={28} color={colors.text.secondary as string} />
                <Text style={styles.emptyText}>{emptyText}</Text>
                {pages.length === 0 && pagesLoaded ? (
                  <TouchableOpacity onPress={() => navigateTo('facebook-messenger-setup')}>
                    <Text style={styles.emptyAction}>{t('posts.connectPage')}</Text>
                  </TouchableOpacity>
                ) : canManage && !debouncedQuery && (status === 'published' || status === 'draft') ? (
                  <TouchableOpacity onPress={() => navigateTo('post-create', undefined, singlePageId ? { pageId: singlePageId } : undefined)}>
                    <Text style={styles.emptyAction}>{t('home.posts.create')}</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            ) : (
              posts.map((post) => (
                <PostCard
                  key={post.postId}
                  post={post}
                  compact
                  onPress={setSelectedPost}
                  onMore={setActionsFor}
                />
              ))
            )}
          </ScrollView>
        )}
      </View>

      {singlePageId && (
        <FacebookInsightsModal visible={showInsights} pageId={singlePageId} onClose={() => setShowInsights(false)} />
      )}

      <PostActionsSheet
        post={actionsFor}
        canManage={canManage}
        onClose={() => setActionsFor(null)}
        onEdit={editPost}
        onChanged={(deleted) => {
          if (deleted) setSelectedPost(null);
          load();
        }}
      />

      {/* No right pane on native/mobile web: the post opens full screen */}
      {Platform.OS !== 'web' && (
        <Modal visible={!!selectedPost} animationType="slide" onRequestClose={() => setSelectedPost(null)}>
          <View style={styles.nativeDetailContainer}>
            <View style={[styles.nativeDetailHeader, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity onPress={() => setSelectedPost(null)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                <DirectionalIcon name="chevron-back" size={22} color={colors.text.primary as string} />
              </TouchableOpacity>
              <Text style={styles.nativeDetailTitle}>{t('posts.post')}</Text>
              <View style={{ width: 22 }} />
            </View>
            {detail}
          </View>
        </Modal>
      )}
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.base },
  titleRow: { alignItems: 'center', gap: spacing.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.sm },
  backButton: { width: 34, height: 34, alignItems: 'center', justifyContent: 'center', borderRadius: 17, borderWidth: 1, borderColor: colors.border },
  titleInfo: { flex: 1, minWidth: 0 },
  title: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.bold, color: colors.text.primary },
  iconButton: { width: 34, height: 34, alignItems: 'center', justifyContent: 'center', borderRadius: 17, borderWidth: 1, borderColor: colors.border },
  iconButtonDisabled: { opacity: 0.4 },
  createButton: { alignItems: 'center', gap: 4, height: 34, paddingHorizontal: spacing.md, borderRadius: 17, backgroundColor: colors.brand.primary },
  createText: { color: colors.text.onPrimary, fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold },
  pageRow: { gap: spacing.xs, paddingHorizontal: spacing.md, paddingBottom: spacing.sm },
  pageChip: { alignItems: 'center', gap: 6, maxWidth: 220, paddingHorizontal: spacing.md, paddingVertical: 6, borderRadius: 16, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  pageChipOn: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary as string, 0.08) },
  pageChipText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, flexShrink: 1 },
  pageChipTextOn: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  pageAvatar: { width: 16, height: 16, borderRadius: 8 },
  tabs: { marginHorizontal: spacing.md, padding: 3, gap: 2, borderRadius: 10, backgroundColor: colors.background.secondary },
  tab: { flex: 1, alignItems: 'center', paddingVertical: spacing.sm, borderRadius: 8 },
  tabOn: { backgroundColor: colors.background.surface },
  tabText: { fontSize: typography.fontSize.sm, color: colors.text.secondary },
  tabTextOn: { color: colors.text.primary, fontFamily: typography.fontFamily.semibold },
  searchBox: {
    alignItems: 'center',
    gap: spacing.sm,
    marginHorizontal: spacing.md,
    marginTop: spacing.sm,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: Platform.OS === 'web' ? spacing.sm : spacing.xs,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  searchInput: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary, ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}) } as any,
  loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.xl },
  list: { flex: 1 },
  listContent: { paddingHorizontal: spacing.md, paddingBottom: spacing.xl, gap: spacing.sm, maxWidth: 640, width: '100%' },
  empty: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xl * 2 },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, textAlign: 'center' },
  emptyAction: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  detailsContainer: { flex: 1 },
  detailsScrollContent: { padding: spacing.md },
  detailsEmpty: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: spacing.sm, padding: spacing.xl },
  detailsEmptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, textAlign: 'center' },
  nativeDetailContainer: { flex: 1, backgroundColor: colors.background.base, paddingTop: Platform.OS === 'ios' ? 50 : 16 },
  nativeDetailHeader: { alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.md, paddingBottom: spacing.sm },
  nativeDetailTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
});
